import { useState } from 'react';
import { Check, Undo2, AlertTriangle, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { slotWhere } from '../../../utils/nurseryRowSlots';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { TagSwatch } from '../TagSwatch';
import {
  SHORT_REASON_LABELS,
  type FreePickLine,
  type FreePickLot,
  type PickList,
  type PickShortReason,
} from '../../../types/nursery';

/**
 * One order line on a load picked without allocating. Nothing was set aside
 * for it: the card lists every place at the despatch farm where its tree
 * stands, in walk order, with what each has free, and the picker says how
 * many they took from which. "Take N" fills the rest from that place in one
 * tap; a different number is typed. When no more can be found the line is
 * closed short, with a reason, and the rest goes on a backorder at despatch.
 *
 * The server checks everything again: never more than ordered, never more
 * than a lot has free, and only at the despatch farm.
 */

export interface FreePickLineCardProps {
  line: FreePickLine;
  loadId: number;
  despatchFarmName: string;
  /** False once the load can no longer be picked. */
  editable: boolean;
  onChanged: (list: PickList) => void;
  /** A scanned label found this lot on this line. */
  highlightedLotId?: number | null;
}

const REASONS = Object.keys(SHORT_REASON_LABELS) as PickShortReason[];
/** Places shown before "Show N more": the first few in the walk are where the picker is going. */
const PLACES_SHOWN = 4;

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function FreePickLineCard({ line, loadId, despatchFarmName, editable, onChanged, highlightedLotId }: FreePickLineCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The lot whose number is being typed or counted, and what.
  const [editing, setEditing] = useState<{ lotId: number; kind: 'qty' | 'count'; value: number | '' } | null>(null);
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState<PickShortReason | ''>('');
  const [notes, setNotes] = useState('');
  const [showAll, setShowAll] = useState(false);

  const id = line.order_line_id;
  const done = line.status === 'picked';
  // A lot with nothing free and nothing taken from it is not somewhere to go.
  const places = line.lots.filter((l) => l.free > 0 || l.pick);
  const highlightedIndex = highlightedLotId != null ? places.findIndex((l) => l.lot_id === highlightedLotId) : -1;
  const visible = showAll || highlightedIndex >= PLACES_SHOWN ? places : places.slice(0, PLACES_SHOWN);

  const run = async (fn: () => Promise<{ success: boolean; data?: PickList } & object>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.success && res.data) {
        onChanged(res.data);
        return true;
      }
      setError(errorMessage(res, fallback));
      return false;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setFrom = (lot: FreePickLot, quantity: number) =>
    run(() => nurseryService.setFreePick(loadId, id, lot.lot_id, { quantity }), 'Could not record the pick');

  /** All this place can give toward what is still wanted, on top of what it already gave. */
  const takeAllFrom = (lot: FreePickLot) => {
    const already = lot.pick?.quantity_picked ?? 0;
    return already + Math.min(line.remaining, lot.free);
  };

  const saveEditing = async (lot: FreePickLot) => {
    if (!editing || editing.value === '') return;
    let ok: boolean;
    if (editing.kind === 'qty') {
      ok = await setFrom(lot, Number(editing.value));
    } else {
      // A count is what stands in the row now; the pick list is read again after.
      setBusy(true);
      setError(null);
      try {
        await nurseryService.correctLotCount(lot.lot_id, { counted: Number(editing.value), load_id: loadId });
        const res = await nurseryService.getPickList(loadId);
        if (res.success) onChanged(res.data);
        ok = true;
      } catch (e) {
        setError(errorMessage(e, 'Could not correct the count'));
        ok = false;
      } finally {
        setBusy(false);
      }
    }
    if (ok) setEditing(null);
  };

  const closeShort = async () => {
    if (!reason) { setError('Choose why the rest cannot be picked.'); return; }
    const ok = await run(() => nurseryService.closeFreeLineShort(loadId, id, { short_reason: reason, notes: notes || null }), 'Could not close the line');
    if (ok) { setClosing(false); setReason(''); setNotes(''); }
  };

  return (
    <li id={`free-${id}`} className={cn('rounded-xl border bg-white p-4',
      done ? 'border-green-200 bg-green-50/40' : line.status === 'short' ? 'border-amber-200 bg-amber-50/40' : 'border-neutral-200')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 break-words">
            {line.sold_as ?? `${line.cultivar_name}${line.rootstock_name ? ` / ${line.rootstock_name}` : ''}`}
          </p>
          <p className="text-sm text-neutral-600 break-words">{line.tree_type_name}</p>
          {line.sold_as && (line.tag_colour_hex || line.tag_colour_name) && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-600 break-words">
              <TagSwatch hex={line.tag_colour_hex} name={line.tag_colour_name} />
              <span>{line.tag_colour_name ? `${line.tag_colour_name} tag` : 'Tag colour'}</span>
            </p>
          )}
          <p className="text-xs text-neutral-500 mt-1 break-words">{line.order_number} · {line.customer_name}</p>
          {line.line_notes && (
            <p className="mt-1 rounded bg-blue-50 border border-blue-200 px-2 py-1 text-sm text-blue-900 break-words">
              {line.line_notes}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold text-neutral-900 leading-none">
            {line.quantity_picked > 0 ? `${line.quantity_picked}/${line.quantity_ordered}` : line.quantity_ordered}
          </p>
          <p className="text-xs text-neutral-500">{line.quantity_picked > 0 ? 'picked' : 'to pick'}</p>
        </div>
      </div>

      {done && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-green-800 font-medium">
          <Check className="h-4 w-4" /> All {line.quantity_ordered} picked
        </p>
      )}
      {line.status === 'short' && (
        <div className="mt-3 text-sm text-amber-800 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words min-w-0">
            <span className="font-medium">Closed short: {line.quantity_picked} of {line.quantity_ordered}.</span>{' '}
            {line.pick_short_reason ? SHORT_REASON_LABELS[line.pick_short_reason] : ''}
            {line.pick_short_notes ? ` · ${line.pick_short_notes}` : ''}
            {line.short_by_name ? ` · ${line.short_by_name}` : ''}
          </span>
          {editable && (
            <Button variant="outline" size="sm" disabled={busy}
              onClick={() => run(() => nurseryService.reopenFreeLine(loadId, id), 'Could not open the line again')}>
              Open again
            </Button>
          )}
        </div>
      )}

      {places.length === 0 ? (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="break-words min-w-0">
            No ready stock of this tree is free at {despatchFarmName}.
            {line.free_elsewhere > 0 ? ` ${line.free_elsewhere} are free at other farms and have to be moved here first.` : ''}
          </span>
        </p>
      ) : (
        <ul className="mt-3 space-y-2" aria-label="Where it stands">
          {visible.map((lot) => {
            const took = lot.pick?.quantity_picked ?? 0;
            const canTake = editable && !done && line.status !== 'short' && lot.free > 0 && line.remaining > 0;
            const isEditing = editing?.lotId === lot.lot_id;
            const what = line.sold_as && lot.cultivar_name
              ? [lot.cultivar_name, lot.rootstock_name].filter(Boolean).join(' / ')
              : null;
            return (
              <li key={lot.lot_id}
                className={cn('rounded-lg border p-3 text-sm', took ? 'border-green-200 bg-white' : 'border-neutral-200',
                  highlightedLotId === lot.lot_id && 'ring-2 ring-primary-500')}>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-neutral-900 break-words">{slotWhere(lot)}</p>
                    <p className="text-xs text-neutral-600 break-words">
                      {lot.lot_code ? `Lot ${lot.lot_code}` : `Lot #${lot.lot_id}`}
                      {what ? ` · ${what}` : ''}
                    </p>
                    {took > 0 && (
                      <p className="text-xs text-green-800 font-medium mt-0.5 break-words">
                        Took {took}{lot.pick?.picked_by_name ? ` · ${lot.pick.picked_by_name}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold text-neutral-900">{lot.free}</p>
                    <p className="text-xs text-neutral-500">free</p>
                  </div>
                </div>

                {editable && !isEditing && (canTake || took > 0) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canTake && (
                      <Button size="sm" disabled={busy} onClick={() => setFrom(lot, takeAllFrom(lot))} className="max-w-full">
                        <Check className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">Take {Math.min(line.remaining, lot.free)}{took ? ' more' : ''}</span>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" disabled={busy}
                      onClick={() => setEditing({ lotId: lot.lot_id, kind: 'qty', value: took || Math.min(line.remaining, lot.free) })}>
                      {took ? 'Change' : 'Other number'}
                    </Button>
                    {took > 0 && (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => setFrom(lot, 0)}>
                        <Undo2 className="h-4 w-4" /> Undo
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => setEditing({ lotId: lot.lot_id, kind: 'count', value: '' })}>
                      Count is wrong
                    </Button>
                  </div>
                )}

                {isEditing && editing && (
                  <div className="mt-2 space-y-2">
                    <label htmlFor={`free-${id}-${lot.lot_id}-${editing.kind}`} className="block text-sm font-medium text-neutral-700">
                      {editing.kind === 'qty'
                        ? `How many did you take from here? (up to ${Math.min(line.quantity_ordered - line.quantity_picked + took, lot.free + took)})`
                        : `How many are in the row now? It says ${lot.lot_count}.`}
                    </label>
                    <input id={`free-${id}-${lot.lot_id}-${editing.kind}`} type="number" inputMode="numeric" min={0}
                      value={editing.value}
                      onChange={(e) => setEditing({ ...editing, value: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })}
                      className={inputCls} />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => { setEditing(null); setError(null); }}>Cancel</Button>
                      <Button size="sm" disabled={busy || editing.value === ''} onClick={() => saveEditing(lot)}>
                        {busy ? 'Saving…' : editing.kind === 'qty' ? 'Save' : 'Save count'}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
          {places.length > visible.length && (
            <li>
              <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                <ChevronDown className="h-4 w-4" /> Show {places.length - visible.length} more place{places.length - visible.length === 1 ? '' : 's'}
              </Button>
            </li>
          )}
          {showAll && places.length > PLACES_SHOWN && (
            <li>
              <Button variant="ghost" size="sm" onClick={() => setShowAll(false)}>
                <ChevronUp className="h-4 w-4" /> Show fewer
              </Button>
            </li>
          )}
        </ul>
      )}

      {error && <p className="mt-3 text-sm text-red-700 break-words">{error}</p>}

      {editable && line.status === 'open' && !closing && (
        <div className="mt-3">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => { setClosing(true); setError(null); }}>
            Can't find the rest…
          </Button>
        </div>
      )}

      {closing && (
        <div className="mt-4 space-y-3 border-t border-neutral-100 pt-3">
          <p className="text-sm text-neutral-800 break-words">
            Close this line at {line.quantity_picked} of {line.quantity_ordered}. The other {line.remaining} go on a backorder when the load is despatched.
          </p>
          <fieldset>
            <legend className="block text-sm font-medium text-neutral-700 mb-1">Why?</legend>
            <div className="space-y-1">
              {REASONS.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm text-neutral-700">
                  <input type="radio" name={`free-reason-${id}`} checked={reason === r} onChange={() => setReason(r)} />
                  {SHORT_REASON_LABELS[r]}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label htmlFor={`free-notes-${id}`} className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
            <input id={`free-notes-${id}`} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => { setClosing(false); setError(null); }}>Cancel</Button>
            <Button size="sm" disabled={busy} onClick={closeShort}>{busy ? 'Saving…' : 'Close the line'}</Button>
          </div>
        </div>
      )}
    </li>
  );
}

export default FreePickLineCard;
